<script setup>
import { computed } from "vue";
import { useStore } from "vuex";

const store = useStore();
const canAddSales = computed(() => store.getters.hasPermission('sales.add'));
const canEditSales = computed(() => store.getters.hasPermission('sales.edit'));
const canDeleteSales = computed(() => store.getters.hasPermission('sales.delete'));
const isSystemInternal = computed(() => String(store.getters.getUserRole || "").toLowerCase() === "system_internal");
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageaddheader
          :title="title"
          :title1="title1"
          :items="canAddSales ? items : []"
        />

        <div class="card">
          <div class="card-body">
            <div class="table-top">
              <div class="search-set">
                <div class="search-input">
                  <a class="btn btn-searchset">
                    <img src="../../../assets/img/icons/search-white.svg" alt="search" />
                  </a>
                  <div class="dataTables_filter">
                    <label>
                      <input
                        v-model="search"
                        type="search"
                        class="form-control form-control-sm"
                        placeholder="Search by bill no, customer, date, item..."
                        @input="searching"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div class="list-stats">
                <span class="stat-chip">
                  <span class="stat-dot dot-primary"></span>
                  Bills: <strong>{{ totalRecords }}</strong>
                </span>
                <span class="stat-chip">
                  <span class="stat-dot dot-success"></span>
                  Showing: <strong>{{ data.length }}</strong>
                </span>
              </div>
            </div>

            <div class="filter-row">
              <div class="filter-group">
                <label class="filter-label">From</label>
                <input v-model="filterFrom" type="date" class="form-control form-control-sm filter-input" @change="applyFilters" />
              </div>
              <div class="filter-group">
                <label class="filter-label">To</label>
                <input v-model="filterTo" type="date" class="form-control form-control-sm filter-input" @change="applyFilters" />
              </div>
              <div class="filter-group">
                <label class="filter-label">Payment Status</label>
                <select v-model="paymentStatus" class="form-control form-control-sm filter-input" @change="applyFilters">
                  <option value="">All</option>
                  <option value="PAID">Paid</option>
                  <option value="PARTIAL">Partial</option>
                  <option value="UNPAID">Unpaid</option>
                </select>
              </div>
              <button class="btn-clear-filter" @click="clearFilters">Clear</button>
            </div>

            <div v-if="loading" class="loading-wrap">
              <div class="loading-spinner"></div>
              <span>Loading sales...</span>
            </div>

            <div v-else-if="!data.length" class="empty-wrap">
              <p class="empty-title">No sales found</p>
              <p class="empty-sub" v-if="search">No results for "{{ search }}"</p>
              <p class="empty-sub" v-else>No sale bills yet.</p>
            </div>

            <div v-else class="table-responsive">
              <a-table
                :columns="displayColumns"
                :data-source="data"
                :loading="loading"
                :pagination="paginationConfig"
                :expanded-row-keys="expandedRowKeys"
                row-key="saleid"
                @change="handleTableChange"
                @expand="handleExpand"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'billno'">
                    <div class="bill-cell">
                      <button class="bill-badge bill-link" @click="viewSale(record.saleid)">{{ record.billno || 'No Bill' }}</button>
                      <small class="bill-id">{{ record.saleid }}</small>
                    </div>
                  </template>

                  <template v-else-if="column.key === 'customer'">
                    <span class="supplier-tag customer-tag">{{ record.customer || '-' }}</span>
                  </template>

                  <template v-else-if="column.key === 'itemcount'">
                    <span class="qty-badge">{{ record.itemcount || 0 }} items</span>
                  </template>

                  <template v-else-if="column.key === 'totalqty'">
                    <span class="qty-badge qty-total">{{ formatQty(record.totalqty, record.totalfreeqty) }}</span>
                  </template>

                  <template v-else-if="column.key === 'netamount'">
                    <span class="price-total">Rs. {{ formatNumber(record.netamount) }}</span>
                  </template>

                  <template v-else-if="column.key === 'payment_status'">
                    <div class="payment-status-cell">
                      <span class="payment-status" :class="paymentStatusClass(record.payment_status)">
                        {{ paymentStatusLabel(record.payment_status) }}
                      </span>
                      <small v-if="record.is_credit" class="credit-note">
                        Paid Rs. {{ formatNumber(record.paid_amount || 0) }}
                      </small>
                    </div>
                  </template>

                  <template v-else-if="column.key === 'gst_report'">
                    <label class="gst-report-toggle" :title="record.include_in_gst_report ? 'Included in SYSTEM_INTERNAL GST report' : 'Excluded from SYSTEM_INTERNAL GST report'">
                      <input
                        type="checkbox"
                        :checked="record.include_in_gst_report"
                        :disabled="gstReportUpdating[record.saleid]"
                        @change="toggleGstReport(record, $event.target.checked)"
                      />
                      <span class="gst-report-slider"></span>
                      <span class="gst-report-label">{{ record.include_in_gst_report ? "Included" : "Excluded" }}</span>
                    </label>
                  </template>

                  <template v-else-if="column.key === 'saledate' || column.key === 'billdate'">
                    <span class="date-text">{{ formatDate(record[column.key]) }}</span>
                  </template>

                  <template v-else-if="column.key === 'action'">
                    <div class="action-btns">
                      <button class="act-btn act-print" title="Print" @click="printSale(record)">
                        <img src="../../../assets/img/icons/printer.svg" alt="print" width="15" height="15" />
                      </button>
                      <button class="act-btn act-send" title="Send Bill" @click="sendSaleBill(record)">
                        <img src="../../../assets/img/icons/mail.svg" alt="send bill" width="15" height="15" />
                      </button>
                      <template v-if="canEditSales || canDeleteSales">
                        <button v-if="canEditSales" class="act-btn act-edit" title="Edit" @click="editSale(record.saleid)">
                          <img src="../../../assets/img/icons/edit.svg" alt="edit" width="15" height="15" />
                        </button>
                        <button v-if="canDeleteSales" class="act-btn act-delete" title="Delete" @click="showAlert(record.saleid)">
                          <img src="../../../assets/img/icons/delete.svg" alt="delete" width="15" height="15" />
                        </button>
                      </template>
                    </div>
                  </template>
                </template>

                <template #expandedRowRender="{ record }">
                  <div class="expanded-wrap">
                    <div v-if="detailLoading[record.saleid]" class="expanded-loading">Loading items...</div>
                    <div v-else-if="saleDetails[record.saleid] && saleDetails[record.saleid].items && saleDetails[record.saleid].items.length">
                      <div class="detail-header">
                        <span>Customer: <strong>{{ saleDetails[record.saleid].customername || '-' }}</strong></span>
                        <span>Bill No: <strong>{{ saleDetails[record.saleid].billno || '-' }}</strong></span>
                        <span>Remarks: <strong>{{ saleDetails[record.saleid].remarks || '-' }}</strong></span>
                      </div>
                      
                      <!-- Sales Items Section -->
                      <div class="section-container">
                        <h4 class="section-title">Sales Items</h4>
                        <table class="detail-table">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Qty</th>
                              <th>Free</th>
                              <th>Rate</th>
                              <th>Discount %</th>
                              <th>Tax Type</th>
                              <th>Tax %</th>
                              <th>Taxable</th>
                              <th>CGST</th>
                              <th>SGST</th>
                              <th>IGST</th>
                              <th>Tax Amt</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="item in detailItems(record.saleid)" :key="item.id">
                              <td>{{ item.itemname || item.itemid }}</td>
                              <td>{{ item.quantity }}</td>
                              <td>{{ item.freequantity || 0 }}</td>
                              <td>Rs. {{ formatNumber(item.rate) }}</td>
                              <td>{{ formatPercent(item.discount) }}</td>
                              <td>{{ item.taxtype || 'GST' }}<span v-if="item.istaxinclusive" class="text-muted"> (Incl)</span></td>
                              <td>{{ item.taxpercent || 0 }}</td>
                              <td>Rs. {{ formatNumber(item.taxablevalue) }}</td>
                              <td>Rs. {{ formatNumber(item.cgst) }}</td>
                              <td>Rs. {{ formatNumber(item.sgst) }}</td>
                              <td>Rs. {{ formatNumber(item.igst) }}</td>
                              <td>Rs. {{ formatNumber(item.taxamount) }}</td>
                            </tr>
                          </tbody>
                          <tfoot v-if="saleDetails[record.saleid]">
                            <tr class="detail-sum-row">
                              <td colspan="11" class="text-end"><strong>Total Amount</strong></td>
                              <td class="text-end"><strong>Rs. {{ formatNumber(saleDetails[record.saleid].totalamount) }}</strong></td>
                            </tr>
                            <tr class="detail-sum-row">
                              <td colspan="11" class="text-end"><strong>Taxable Value</strong></td>
                              <td class="text-end"><strong>Rs. {{ formatNumber(sumField(saleDetails[record.saleid].items, 'taxablevalue')) }}</strong></td>
                            </tr>
                            <tr class="detail-sum-row">
                              <td colspan="11" class="text-end"><strong>CGST</strong></td>
                              <td class="text-end"><strong>Rs. {{ formatNumber(sumField(saleDetails[record.saleid].items, 'cgst')) }}</strong></td>
                            </tr>
                            <tr class="detail-sum-row">
                              <td colspan="11" class="text-end"><strong>SGST</strong></td>
                              <td class="text-end"><strong>Rs. {{ formatNumber(sumField(saleDetails[record.saleid].items, 'sgst')) }}</strong></td>
                            </tr>
                            <tr class="detail-sum-row">
                              <td colspan="11" class="text-end"><strong>IGST</strong></td>
                              <td class="text-end"><strong>Rs. {{ formatNumber(sumField(saleDetails[record.saleid].items, 'igst')) }}</strong></td>
                            </tr>
                            <tr class="detail-sum-row">
                              <td colspan="11" class="text-end"><strong>Tax Amount</strong></td>
                              <td class="text-end"><strong>Rs. {{ formatNumber(saleDetails[record.saleid].taxamount) }}</strong></td>
                            </tr>
                            <tr class="detail-sum-row">
                              <td colspan="11" class="text-end"><strong>Discount</strong></td>
                              <td class="text-end"><strong>- Rs. {{ formatNumber(saleDetails[record.saleid].discountamount) }}</strong></td>
                            </tr>
                            <tr class="detail-sum-row">
                              <td colspan="11" class="text-end"><strong>Round Off</strong></td>
                              <td class="text-end"><strong>Rs. {{ formatNumber(saleDetails[record.saleid].roundoff) }}</strong></td>
                            </tr>
                            <tr class="detail-sum-row detail-sum-row-net">
                              <td colspan="11" class="text-end"><strong>Net Amount</strong></td>
                              <td class="text-end"><strong>Rs. {{ formatNumber(saleDetails[record.saleid].netamount) }}</strong></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      <!-- Sales Returns Section -->
                      <div v-if="saleDetails[record.saleid] && saleDetails[record.saleid].salesreturns && saleDetails[record.saleid].salesreturns.length" class="section-container returns-section">
                        <h4 class="section-title">Sales Returns</h4>
                        <div v-for="returnRecord in saleDetails[record.saleid].salesreturns" :key="returnRecord.returnid" class="return-item">
                          <div class="return-header">
                            <span class="return-bill-no">Return Bill: {{ returnRecord.billno || '-' }}</span>
                            <span class="return-date">Date: {{ formatDate(returnRecord.returndate) }}</span>
                            <span class="return-status" :class="getStatusClass(returnRecord.status)">{{ returnRecord.status || '-' }}</span>
                          </div>
                          <table class="detail-table return-table">
                            <thead>
                              <tr>
                                <th>Item</th>
                                <th>Return Qty</th>
                                <th>Rate</th>
                                <th>Discount %</th>
                                <th>Tax %</th>
                                <th>Tax Amt</th>
                                <th>Reason</th>
                                <th>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="returnItem in returnRecord.items" :key="returnItem.id">
                                <td>{{ returnItem.itemname || returnItem.itemid }}</td>
                                <td>{{ returnItem.returnqty }}</td>
                                <td>Rs. {{ formatNumber(returnItem.rate) }}</td>
                                <td>{{ formatPercent(returnItem.discount) }}</td>
                                <td>{{ returnItem.taxpercent || 0 }}</td>
                                <td>Rs. {{ formatNumber(returnItem.taxamount) }}</td>
                                <td>{{ returnItem.reason || '-' }}</td>
                                <td>Rs. {{ formatNumber(returnRowTotal(returnItem)) }}</td>
                              </tr>
                            </tbody>
                            <tfoot>
                              <tr class="detail-sum-row">
                                <td colspan="7" class="text-end"><strong>Taxable Value</strong></td>
                                <td class="text-end"><strong>Rs. {{ formatNumber(sumField(returnRecord.items, 'taxablevalue')) }}</strong></td>
                              </tr>
                              <tr class="detail-sum-row">
                                <td colspan="7" class="text-end"><strong>CGST</strong></td>
                                <td class="text-end"><strong>Rs. {{ formatNumber(sumField(returnRecord.items, 'cgst')) }}</strong></td>
                              </tr>
                              <tr class="detail-sum-row">
                                <td colspan="7" class="text-end"><strong>SGST</strong></td>
                                <td class="text-end"><strong>Rs. {{ formatNumber(sumField(returnRecord.items, 'sgst')) }}</strong></td>
                              </tr>
                              <tr class="detail-sum-row">
                                <td colspan="7" class="text-end"><strong>IGST</strong></td>
                                <td class="text-end"><strong>Rs. {{ formatNumber(sumField(returnRecord.items, 'igst')) }}</strong></td>
                              </tr>
                              <tr class="detail-sum-row">
                                <td colspan="7" class="text-end"><strong>Tax Amount</strong></td>
                                <td class="text-end"><strong>Rs. {{ formatNumber(sumField(returnRecord.items, 'taxamount')) }}</strong></td>
                              </tr>
                              <tr class="detail-sum-row">
                                <td colspan="7" class="text-end"><strong>Return Total</strong></td>
                                <td class="text-end"><strong>Rs. {{ formatNumber(returnRecord.netamount) }}</strong></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div v-else class="expanded-loading">No items found.</div>
                  </div>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { openPdfPreview } from "@/assets/utils/pdfPreview";
import { sendBillViaChannel } from "../../../services/billDelivery";
import { fetchSaleInvoicePdf } from "@/services/invoicePdf";
import { normalizePhoneForWhatsApp } from "@/utils/phone";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const columns = [
  { title: "Bill No", dataIndex: "billno", key: "billno" },
  { title: "Customer", dataIndex: "customer", key: "customer" },
  { title: "Sale Date", dataIndex: "saledate", key: "saledate" },
  { title: "Bill Date", dataIndex: "billdate", key: "billdate" },
  { title: "Items", dataIndex: "itemcount", key: "itemcount" },
  { title: "Total Qty", dataIndex: "totalqty", key: "totalqty" },
  { title: "Net Amount", dataIndex: "netamount", key: "netamount" },
  { title: "Payment Status", dataIndex: "payment_status", key: "payment_status" },
  { title: "GST Report", dataIndex: "include_in_gst_report", key: "gst_report", width: 150 },
  { title: "Action", key: "action", width: 130 },
];

export default {
  name: "saleslist",
  data() {
    return {
      title: "Sales List",
      title1: "Manage sales by bill",
      items: [{ text: "New Sale", addname: "/add-sales" }],
      columns,
      data: [],
      loading: false,
      search: "",
      filterFrom: "",
      filterTo: "",
      paymentStatus: "",
      searchTimer: null,
      expandedRowKeys: [],
      saleDetails: {},
      detailLoading: {},
      gstReportUpdating: {},
      itemCatalog: {},
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
    };
  },
  computed: {
    totalRecords() {
      return this.pagination.total || 0;
    },
    paginationConfig() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} bills`,
      };
    },
    isSystemInternalUser() {
      return String(this.$store?.getters?.getUserRole || "").toLowerCase() === "system_internal";
    },
    displayColumns() {
      if (this.isSystemInternalUser) return this.columns;
      return this.columns.filter((column) => column.key !== "gst_report");
    },
  },
  methods: {
    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    searching() {
      this.pagination.current = 1;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.getSales(), 300);
    },
    applyFilters() {
      this.pagination.current = 1;
      this.getSales();
    },
    clearFilters() {
      this.search = "";
      this.filterFrom = "";
      this.filterTo = "";
      this.paymentStatus = "";
      this.pagination.current = 1;
      this.getSales();
    },
    formatDate(val) {
      if (!val) return "-";
      const d = new Date(val);
      if (isNaN(d)) return val;
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatNumber(val) {
      if (val === null || val === undefined || val === "") return "-";
      return Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatPercent(val) {
      if (val === null || val === undefined || val === "") return "-";
      return `${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
    },
    formatQty(qty, freeqty) {
      const base = Number(qty || 0);
      const free = Number(freeqty || 0);
      return free ? `${base} + ${free}` : `${base}`;
    },
    sumField(items, field) {
      return (items || []).reduce((sum, item) => sum + Number(item?.[field] || 0), 0);
    },
    rowTotal(item) {
      const amount = Number(item.amount || 0);
      const tax = Number(item.taxamount || 0);
      return item.istaxinclusive ? amount : (amount + tax);
    },
    returnRowTotal(returnItem) {
      return Number(returnItem.amount || 0) + Number(returnItem.taxamount || 0);
    },
    getStatusClass(status) {
      switch (status?.toLowerCase()) {
        case 'confirmed':
          return 'status-confirmed';
        case 'cancelled':
          return 'status-cancelled';
        case 'draft':
          return 'status-draft';
        default:
          return 'status-default';
      }
    },
    paymentStatusLabel(status) {
      const normalized = (status || "PAID").toUpperCase();
      if (normalized === "PARTIAL") return "Partial";
      if (normalized === "UNPAID") return "Unpaid";
      return "Paid";
    },
    paymentStatusClass(status) {
      const normalized = (status || "PAID").toUpperCase();
      return {
        "payment-paid": normalized === "PAID",
        "payment-partial": normalized === "PARTIAL",
        "payment-unpaid": normalized === "UNPAID",
      };
    },
    async toggleGstReport(record, includeInGstReport) {
      const previousValue = Boolean(record.include_in_gst_report);
      record.include_in_gst_report = includeInGstReport;
      this.gstReportUpdating = { ...this.gstReportUpdating, [record.saleid]: true };
      try {
        await axios.patch(`/v1/sales/${record.saleid}/gst-report`, {
          include_in_gst_report: includeInGstReport,
        });
      } catch (error) {
        record.include_in_gst_report = previousValue;
        Swal.fire({
          icon: "error",
          title: "Could not update GST report status",
          text: error.response?.data?.detail || "Only SYSTEM_INTERNAL can change this setting.",
        });
      } finally {
        this.gstReportUpdating = { ...this.gstReportUpdating, [record.saleid]: false };
      }
    },
    async hydrateItemNames(detail) {
      const missingIds = (detail.items || [])
        .filter((item) => !this.itemCatalog[item.itemid])
        .map((item) => item.itemid);

      await Promise.all(
        [...new Set(missingIds)].map(async (itemid) => {
          try {
            const res = await axios.get(`/v1/sales/items/${itemid}`);
            if (res.data?.itemid) this.itemCatalog[itemid] = res.data.itemname;
          } catch (error) {
            console.error(error);
          }
        })
      );

      detail.items = (detail.items || []).map((item) => ({
        ...item,
        itemname: this.itemCatalog[item.itemid] || item.itemname || item.itemid,
      }));
    },
    async fetchSaleDetail(saleid) {
      if (this.saleDetails[saleid] || this.detailLoading[saleid]) return;
      this.detailLoading = { ...this.detailLoading, [saleid]: true };
      try {
        const res = await axios.get(`/v1/sales/${saleid}`);
        await this.hydrateItemNames(res.data);
        this.saleDetails = { ...this.saleDetails, [saleid]: res.data };
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load sale items." });
      } finally {
        this.detailLoading = { ...this.detailLoading, [saleid]: false };
      }
    },
    // Contact details for the send dialog, from the saved sale.
    async saleContact(record) {
      const saleid = record?.saleid;
      if (!saleid) return null;
      await this.fetchSaleDetail(saleid);
      const detail = this.saleDetails[saleid];
      if (!detail) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load the sale." });
        return null;
      }
      const billNo = detail.billno || "";
      return {
        billNo,
        documentTitle: `Tax Invoice ${billNo}`.trim(),
        partyName: detail.customername || record.customer || "Customer",
        email: detail.email || record.email || "",
        phoneNumber: detail.mobile || record.mobile || "",
      };
    },
    // The invoice PDF is generated by the server from the saved bill.
    async printSale(record) {
      if (!record?.saleid) return;
      Swal.fire({ title: "Preparing invoice...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      try {
        const { blob, fileName } = await fetchSaleInvoicePdf(record.saleid);
        Swal.close();
        openPdfPreview(blob, { title: `Invoice ${record.billno || ""}`.trim(), fileName });
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Print Error", text: "Could not load the invoice PDF." });
      }
    },
    async sendSaleBill(record) {
      const bill = await this.saleContact(record);
      if (!bill) return;

      const emailValue = this.escapeHtml(bill.email);
      const phoneValue = this.escapeHtml(bill.phoneNumber);
      const { value: delivery } = await Swal.fire({
        title: "Send bill",
        html: `
          <div class="text-start">
            <label style="display:block;margin-bottom:8px;">
              <input type="checkbox" id="send-email" ${emailValue ? "checked" : ""} />
              Email
            </label>
            <input id="recipient-email" type="email" class="swal2-input" placeholder="customer@example.com" value="${emailValue}" style="margin:0 0 14px;width:100%;" />
            <label style="display:block;margin-bottom:8px;">
              <input type="checkbox" id="send-whatsapp" ${phoneValue ? "checked" : ""} />
              WhatsApp
            </label>
            <input id="recipient-phone" type="tel" class="swal2-input" placeholder="+919876543210" value="${phoneValue}" style="margin:0;width:100%;" />
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Send",
        preConfirm: () => {
          const sendEmail = document.getElementById("send-email").checked;
          const sendWhatsapp = document.getElementById("send-whatsapp").checked;
          const email = document.getElementById("recipient-email").value.trim();
          const phoneNumber = document.getElementById("recipient-phone").value.trim();
          const normalizedPhone = normalizePhoneForWhatsApp(phoneNumber);

          if (!sendEmail && !sendWhatsapp) {
            Swal.showValidationMessage("Select email, WhatsApp, or both.");
            return false;
          }
          if (sendEmail && !email) {
            Swal.showValidationMessage("Email is required.");
            return false;
          }
          if (sendWhatsapp && !phoneNumber) {
            Swal.showValidationMessage("Phone number is required.");
            return false;
          }
          if (sendWhatsapp && !normalizedPhone.valid) {
            Swal.showValidationMessage("Enter a valid WhatsApp phone number.");
            return false;
          }
          return { sendEmail, sendWhatsapp, email, phoneNumber: normalizedPhone.digits };
        },
      });
      if (!delivery) return;

      try {
        Swal.fire({ title: "Sending bill...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        // No document/HTML is sent: the server builds the PDF from the saved sale.
        const documentPayload = {};
        const sendTasks = [];
        if (delivery.sendEmail) {
          sendTasks.push(sendBillViaChannel("email", {
            recipient: delivery.email,
            subject: `${bill.documentTitle}`,
            message: `Please find attached ${bill.documentTitle}.`,
            reference_type: "SALE",
            reference_id: record.saleid,
            message_type: "BILL",
            ...documentPayload,
          }));
        }
        if (delivery.sendWhatsapp) {
          sendTasks.push(sendBillViaChannel("whatsapp", {
            phone_number: delivery.phoneNumber,
            customer_name: bill.partyName,
            order_number: bill.billNo,
            caption: `Please find attached ${bill.documentTitle}.`,
            reference_type: "SALE",
            reference_id: record.saleid,
            message_type: "BILL",
            ...documentPayload,
          }));
        }
        await Promise.all(sendTasks);

        const sentTo = [
          delivery.sendEmail ? `email ${delivery.email}` : "",
          delivery.sendWhatsapp ? `WhatsApp ${delivery.phoneNumber}` : "",
        ].filter(Boolean).join(" and ");
        Swal.fire({ icon: "success", title: "Sent", text: `Bill sent by ${sentTo}.`, timer: 1800, showConfirmButton: false });
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Send Error", text: error.response?.data?.detail || "Could not send the bill." });
      }
    },
    handleExpand(expanded, record) {
      if (expanded) {
        this.expandedRowKeys = [...new Set([...this.expandedRowKeys, record.saleid])];
        this.fetchSaleDetail(record.saleid);
      } else {
        this.expandedRowKeys = this.expandedRowKeys.filter((id) => id !== record.saleid);
      }
    },
    detailItems(saleid) {
      return this.saleDetails[saleid]?.items || [];
    },
    async getSales() {
      this.loading = true;
      try {
        const res = await axios.get("/v1/sales", {
          params: {
            page: this.pagination.current,
            page_size: this.pagination.pageSize,
            search: this.search.trim() || undefined,
            date_from: this.filterFrom || undefined,
            date_to: this.filterTo || undefined,
            payment_status: this.paymentStatus || undefined,
          },
        });
        this.data = res.data.items || [];
        this.pagination = {
          current: res.data.page || this.pagination.current,
          pageSize: res.data.page_size || this.pagination.pageSize,
          total: res.data.total || 0,
        };
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load sales." });
      } finally {
        this.loading = false;
      }
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.getSales();
    },
    editSale(saleid) {
      this.$router.push({ path: "/edit-sales", query: { id: saleid } });
    },
    viewSale(saleid) {
      this.$router.push({ path: "/sales-details", query: { id: saleid } });
    },
    showAlert(id) {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#FF9F43",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (!result.isConfirmed) return;
        axios.delete(`/v1/sales/${id}`)
          .then(() => {
            this.data = this.data.filter((row) => row.saleid !== id);
            this.pagination.total = Math.max(0, this.pagination.total - 1);
            this.expandedRowKeys = this.expandedRowKeys.filter((rowId) => rowId !== id);
            Swal.fire({ icon: "success", title: "Deleted!", text: "Sale bill has been deleted.", timer: 1500, showConfirmButton: false });
          })
          .catch((error) => {
            Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Could not delete." });
          });
      });
    },
  },
  beforeMount() {
    this.getSales();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
};
</script>

<style scoped>
.table-top { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:16px; }
.search-input { display:flex; align-items:center; gap:6px; }
.search-input .form-control { width:320px; height:36px; border-radius:6px; border:1px solid #e5e7eb; padding:0 12px; }
.list-stats { display:flex; gap:10px; align-items:center; }
.stat-chip { display:flex; align-items:center; gap:5px; font-size:0.82rem; color:#6b7280; background:#f9fafb; border:1px solid #e5e7eb; border-radius:20px; padding:3px 10px; }
.stat-dot { width:7px; height:7px; border-radius:50%; }
.dot-primary { background:#fb923c; }
.dot-success { background:#22c55e; }
.filter-row { display:flex; gap:10px; align-items:end; flex-wrap:wrap; margin-bottom:16px; }
.filter-group { display:flex; flex-direction:column; gap:4px; }
.filter-label { font-size:0.75rem; color:#6b7280; font-weight:600; }
.filter-input { width:150px; height:36px; border-radius:6px; border:1px solid #e5e7eb; }
.btn-clear-filter { height:36px; padding:0 14px; border:none; border-radius:6px; background:#f3f4f6; color:#374151; font-size:0.85rem; font-weight:600; }
.loading-wrap, .empty-wrap { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:50px 20px; gap:10px; }
.loading-spinner { width:36px; height:36px; border:3px solid #f3f4f6; border-top-color:#fb923c; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.empty-title { font-size:1rem; font-weight:700; color:#111827; margin:0; }
.empty-sub { font-size:0.84rem; color:#6b7280; margin:0; }
.bill-cell { display:flex; flex-direction:column; gap:4px; }
.bill-badge { display:inline-block; background:#fff7f0; color:#ea580c; border:1px solid #fdba74; border-radius:20px; padding:2px 10px; font-size:.78rem; font-weight:700; width:max-content; }
.bill-link { cursor:pointer; line-height:1.4; }
.bill-link:hover { background:#ffedd5; color:#c2410c; }
.bill-id { color:#9ca3af; font-size:.72rem; }
.supplier-tag { display:inline-block; padding:2px 10px; border-radius:20px; font-size:.78rem; font-weight:500; }
.customer-tag { background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; }
.qty-badge { display:inline-block; background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; border-radius:20px; padding:2px 10px; font-size:.78rem; font-weight:600; }
.qty-total { background:#ecfeff; color:#0f766e; border-color:#99f6e4; }
.date-text { font-size:.82rem; color:#6b7280; }
.price-total { color:#ea580c; font-weight:700; }
.payment-status-cell { display:flex; flex-direction:column; gap:4px; align-items:flex-start; }
.payment-status { display:inline-block; border-radius:20px; padding:2px 10px; font-size:.76rem; font-weight:700; text-transform:uppercase; }
.payment-paid { background:#dcfce7; color:#166534; border:1px solid #86efac; }
.payment-partial { background:#fef3c7; color:#92400e; border:1px solid #fcd34d; }
.payment-unpaid { background:#fee2e2; color:#b91c1c; border:1px solid #fca5a5; }
.gst-report-toggle { display:inline-flex; align-items:center; gap:8px; margin:0; cursor:pointer; user-select:none; }
.gst-report-toggle input { position:absolute; opacity:0; pointer-events:none; }
.gst-report-slider { position:relative; width:38px; height:20px; flex:0 0 38px; border-radius:999px; background:#e5e7eb; border:1px solid #d1d5db; transition:background .15s ease, border-color .15s ease; }
.gst-report-slider::after { content:""; position:absolute; top:2px; left:2px; width:14px; height:14px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(15,23,42,.2); transition:transform .15s ease; }
.gst-report-toggle input:checked + .gst-report-slider { background:#dcfce7; border-color:#86efac; }
.gst-report-toggle input:checked + .gst-report-slider::after { transform:translateX(18px); }
.gst-report-toggle input:disabled + .gst-report-slider { opacity:.65; }
.gst-report-label { min-width:60px; font-size:.76rem; font-weight:700; color:#374151; }
.credit-note { color:#6b7280; font-size:.72rem; }
.action-btns { display:flex; gap:6px; }
.act-btn { width:30px; height:30px; display:inline-flex; align-items:center; justify-content:center; border:none; border-radius:6px; }
.act-print { background:#f3f4f6; }
.act-send { background:#ecfdf5; }
.act-edit { background:#eff6ff; }
.act-delete { background:#fef2f2; }
.expanded-wrap { background:#fffaf5; border:1px solid #fed7aa; border-radius:10px; padding:14px; }
.detail-header { display:flex; gap:18px; flex-wrap:wrap; margin-bottom:10px; font-size:.82rem; color:#6b7280; }
.detail-table { width:100%; border-collapse:collapse; }
.detail-table th, .detail-table td { padding:8px 10px; border-bottom:1px solid #f3e8d8; font-size:.82rem; text-align:left; }
.detail-table th { color:#6b7280; font-weight:700; text-transform:uppercase; font-size:.72rem; }
.detail-table tfoot td { border-bottom:none; border-top:1px solid #f3e8d8; }
.detail-sum-row td { background:#fff7f0; }
.detail-sum-row-net td { background:#ffedd5; }
.expanded-loading { color:#6b7280; font-size:.82rem; }
.section-container { margin-bottom:20px; }
.section-title { font-size:1rem; font-weight:600; color:#374151; margin:0 0 10px 0; padding-bottom:5px; border-bottom:2px solid #f3e8d8; }
.returns-section { border-top:2px dashed #f3e8d8; padding-top:15px; }
.return-item { margin-bottom:15px; border:1px solid #e5e7eb; border-radius:8px; overflow:hidden; }
.return-header { display:flex; gap:15px; align-items:center; padding:10px 15px; background:#f9fafb; border-bottom:1px solid #e5e7eb; font-size:.82rem; }
.return-bill-no { font-weight:600; color:#374151; }
.return-date { color:#6b7280; }
.return-status { padding:2px 8px; border-radius:12px; font-size:.72rem; font-weight:600; text-transform:uppercase; }
.status-confirmed { background:#dcfce7; color:#166534; }
.status-cancelled { background:#fee2e2; color:#dc2626; }
.status-draft { background:#fef3c7; color:#92400e; }
.status-default { background:#f3f4f6; color:#6b7280; }
.return-table { margin-bottom:0; }
.return-table th, .return-table td { padding:6px 10px; font-size:.78rem; }
</style>
